import { Form } from "@inertiajs/react"
import { CheckCircle2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { AutoChecksPanel } from "@/components/submissions/auto-checks-panel"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useOnline } from "@/hooks/use-online"
import { approveMissionCollabSubmissionPath } from "@/routes"
import type { Submission } from "@/types/submissions"

/**
 * OKS-35 / OKS-399 — Brand-side approve confirm dialog (UX Spec §5).
 *
 * Inertia `<Form>` POST. Idempotent re-fire returns 200 +
 * `submissions.idempotent.approved` flash, so the dialog can safely
 * re-submit on accidental double-click (the second POST is a no-op
 * server-side, and NEVER creates a kudo — FR-8).
 *
 * OKS-399 — when `canSendKudo`, the dialog grows an advisory auto-checks
 * panel + a **checked-by-default** "Also send a kudo" composer (checkbox
 * + pre-filled message textarea with the OKS-396 counter idiom). All
 * **plain shadcn `Checkbox`/`Textarea`/`Label` with `name` attrs** —
 * NO shadcn Form / RHF / zod (Crew rule).
 *
 * OKS-920 — this dialog renders NO server-side inline errors, by design.
 * Approve's only user-reachable failure is `:invalid_state` (someone else
 * actioned it first), which is an INVALIDATED action: no retry can succeed,
 * so it closes with a destructive toast (PRD Rule E) and the controller ships
 * no error bag at all. The former `errors["approve.kudo_message"]` and
 * `errors.base` renders were removed as unreachable. The only in-dialog error
 * affordance left is the CLIENT-side over-limit hint.
 *
 * Locked testids: `approve-kudo-checkbox`, `approve-kudo-message`,
 * `approve-kudo-counter`, `approve-kudo-error` (+ existing OKS-35 ids).
 */
const KUDO_MAX_LENGTH = 280

interface Props {
  submission: Submission
  missionId: number
  creatorHandle: string | null
  canSendKudo?: boolean
  trigger?: React.ReactElement
  /**
   * OKS-708 — optional POST target override. Defaults to the brand-nested
   * `approveMissionCollabSubmissionPath`; the agency cross-brand home
   * passes its server-supplied `/agency/submissions/:id/approve` URL so the
   * action resolves the brand from the record. Backward-compatible.
   */
  action?: string
}

export function ApproveSubmissionConfirmDialog({
  submission,
  missionId,
  creatorHandle,
  canSendKudo = false,
  trigger,
  action,
}: Props) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)

  // Checked by default (Q-3 / PRD #18 / FR-2) — friction is in opting
  // OUT, not in. Pre-fill the message with the i18n default (FR-9).
  const [sendKudo, setSendKudo] = useState(true)
  const [message, setMessage] = useState(() =>
    t("submissions.brand.confirm.kudo.default_message"),
  )

  const length = message.length
  const overLimit = length > KUDO_MAX_LENGTH
  const atLimit = length === KUDO_MAX_LENGTH
  const blank = message.trim().length === 0
  // Approve is blocked only when the kudo is checked AND the message is
  // unusable (blank / over-limit). Unchecked → message is irrelevant.
  const kudoBlocksSubmit = canSendKudo && sendKudo && (blank || overLimit)

  const ariaTitle = creatorHandle
    ? `@${creatorHandle}`
    : t("submissions.brand.empty_title")

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          trigger ?? (
            <Button
              type="button"
              variant="default"
              size="sm"
              aria-label={t("submissions.brand.confirm.approve_title")}
              data-testid={`approve-submission-trigger-${submission.id}`}
            >
              <CheckCircle2 aria-hidden="true" className="mr-1 size-4" />
              {t("submissions.brand.confirm.approve_cta")}
            </Button>
          )
        }
      />
      <DialogContent
        data-testid={`approve-submission-dialog-${submission.id}`}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>
            {t("submissions.brand.confirm.approve_title", {
              handle: ariaTitle,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("submissions.brand.confirm.approve_body")}
          </DialogDescription>
        </DialogHeader>

        {/* Advisory auto-checks (decision support BEFORE the choice).
            Shown whenever the backend computed them — independent of
            kudo rights. */}
        {submission.auto_checks && (
          <AutoChecksPanel autoChecks={submission.auto_checks} />
        )}

        <Form
          action={
            action ??
            approveMissionCollabSubmissionPath(
              missionId,
              submission.collab_id,
              submission.id,
            )
          }
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={() => setOpen(false)}
        >
          {({ processing }) => (
            <div className="flex flex-col gap-3">
              {canSendKudo && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`approve-kudo-checkbox-${submission.id}`}
                      // `name` makes Radix emit a hidden form input. When
                      // checked it submits `approve[send_kudo]=on`; when
                      // unchecked it is omitted → BE `.present?` is false.
                      name="approve[send_kudo]"
                      checked={sendKudo}
                      onCheckedChange={(next) => setSendKudo(next === true)}
                      data-testid="approve-kudo-checkbox"
                    />
                    <Label htmlFor={`approve-kudo-checkbox-${submission.id}`}>
                      {t("submissions.brand.confirm.kudo.checkbox_label")}
                    </Label>
                  </div>

                  {/* Composer collapses when unchecked (UX Spec §"Form
                      wiring") — when hidden, the textarea isn't in the
                      DOM so `kudo_message` is omitted from the POST. */}
                  {sendKudo && (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="approve-kudo-message">
                        {t("submissions.brand.confirm.kudo.message_label")}
                      </Label>
                      <Textarea
                        id="approve-kudo-message"
                        name="approve[kudo_message]"
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t(
                          "submissions.brand.confirm.kudo.placeholder",
                        )}
                        disabled={processing}
                        aria-describedby="approve-kudo-counter approve-kudo-error"
                        // OKS-920 — client-side only; approve ships no error
                        // bag, so there is no server error key to consult.
                        aria-invalid={overLimit ? "true" : undefined}
                        data-testid="approve-kudo-message"
                        className="resize-y"
                      />
                      <p
                        id="approve-kudo-counter"
                        data-testid="approve-kudo-counter"
                        className={
                          "self-end text-xs tabular-nums " +
                          (overLimit
                            ? "text-red-600 dark:text-red-400"
                            : atLimit
                              ? "text-amber-600 dark:text-amber-400"
                              : "text-muted-foreground")
                        }
                      >
                        {length}/{KUDO_MAX_LENGTH}
                      </p>
                      <div
                        id="approve-kudo-error"
                        data-testid="approve-kudo-error"
                        role="alert"
                        aria-live="polite"
                      >
                        {/* OKS-920 — the client-side over-limit hint is the
                            ONLY thing that renders here. The server-side
                            `errors["approve.kudo_message"]` <InputError> was
                            removed: it was unreachable twice over — confirm is
                            `disabled` while the kudo is blank or over-limit
                            (see `kudoBlocksSubmit` → the submit button), so a
                            `:kudo_invalid` can't be produced through the UI
                            (measured, OKS-920 M2b); and #approve no longer
                            emits an error bag at all (its only reachable
                            failure is invalidated → toast + close). A forged
                            POST still hits the service validation; that is
                            covered server-side by `approve_kudo_spec.rb`,
                            which asserts the RESPONSE rather than this
                            markup. */}
                        {overLimit && (
                          <p className="text-sm text-red-600 dark:text-red-400">
                            {t(
                              "submissions.brand.confirm.kudo.over_limit_hint",
                              { limit: KUDO_MAX_LENGTH },
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* OKS-920 — the `errors.base` <InputError> was removed here.
                  Approve's only user-reachable failure is `:invalid_state`
                  (someone else actioned it first — measured M2 via a two-tab
                  race, no fault injection). That is an INVALIDATED action:
                  no retry can succeed, so the dialog closes with a
                  destructive toast (PRD Rule E) and there is nothing left on
                  screen to render into. #approve therefore ships no error bag
                  at all, which makes this render unreachable by transport as
                  well as by UX. Do NOT re-add it without also giving approve
                  a correctable failure — see
                  `BrandSubmissionsController#redirect_after_brand_action`. */}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  data-testid={`approve-submission-cancel-${submission.id}`}
                >
                  {t("submissions.brand.confirm.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={processing || kudoBlocksSubmit || !online}
                  autoFocus
                  data-testid={`approve-submission-confirm-${submission.id}`}
                >
                  {t("submissions.brand.confirm.approve_cta")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}
